import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { logger } from '@/lib/logger';
import AppLayout from '@/components/layout/AppLayout';
import { Spinner } from '@phosphor-icons/react';
import { AdminStatsCards } from './admin/AdminStatsCards';
import { BillingSettingsCard } from './admin/BillingSettingsCard';
import { InboundEmailCard } from './admin/InboundEmailCard';
import { UserManagementTable } from './admin/UserManagementTable';

export default function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [billing, setBilling] = useState({});
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [editBilling, setEditBilling] = useState(null);
  const [saving, setSaving] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(null);
  const [inbound, setInbound] = useState(null);
  const [verify, setVerify] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [uRes, bRes, sRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/billing'),
        api.get('/admin/stats'),
      ]);
      setUsers(uRes.data.users);
      setBilling(bRes.data.billing);
      setStats(sRes.data);
      api.get('/admin/inbound-status').then(r => setInbound(r.data)).catch((err) => logger.debug('inbound-status load failed:', err));
    } catch (err) {
      logger.error('Admin load failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const verifySetup = async () => {
    setVerifying(true);
    setVerify(null);
    try {
      const { data } = await api.get('/admin/inbound/verify-setup');
      setVerify(data);
    } catch (err) {
      setVerify({ ok: false, error: err.response?.data?.detail || 'Verification failed' });
    } finally {
      setVerifying(false);
    }
  };

  const saveBilling = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/admin/billing', editBilling);
      setBilling(data.billing);
      setEditBilling(null);
    } catch (err) {
      alert('Failed to save billing settings');
    } finally {
      setSaving(false);
    }
  };

  const updateUser = async (userId, updates) => {
    setUpdatingUser(userId);
    try {
      await api.put(`/admin/users/${userId}`, updates);
      await loadAll();
    } catch (err) {
      alert('Failed to update user');
    } finally {
      setUpdatingUser(null);
    }
  };

  if (user?.role !== 'admin') return <AppLayout><div className="p-8 text-center text-[#6B7280] font-ibm">Access denied. Admin only.</div></AppLayout>;
  if (loading) return <AppLayout><div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-[#002FA7]" /></div></AppLayout>;

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto p-6 sm:p-8" data-testid="admin-page">
        <h1 className="font-outfit text-3xl font-bold text-[#09090B] mb-8">Admin Dashboard</h1>
        <AdminStatsCards stats={stats} />
        <BillingSettingsCard billing={billing} editBilling={editBilling} setEditBilling={setEditBilling} saving={saving} saveBilling={saveBilling} />
        <InboundEmailCard inbound={inbound} verify={verify} verifying={verifying} verifySetup={verifySetup} />
        <UserManagementTable users={users} updatingUser={updatingUser} updateUser={updateUser} />
      </div>
    </AppLayout>
  );
}
